import { useMemo } from 'react';
import {
  EventHeaderIconWithHoverEffect,
  EventHeaderMarketplacesWithMetrics,
  EventHeaderMetricsType,
  EventHeaderMyEntityToggleItem,
  EventHeaderSidePanelSwiper,
  EventHeaderTableColumnAction,
} from 'src/components/Events/EventHeader';
import { TabItem } from 'src/components/Events/EventPage/EventPageTabs';
import { BulkActionToggleButton } from 'src/components/Listings/InventoryEventPage/InventoryEventPageRework/EventHeader/EventActions/BulkAction';
import { useMarketplaceEventInfo } from 'src/components/Listings/InventoryEventPage/InventoryEventPageRework/InventoryEventLocalContext.hooks';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { ToggleGroup, ToggleItem } from 'src/core/ui/ToggleGroup';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { CustomPurchasesColumn } from 'src/utils/columns/purchases/purchasesCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { getInventorySummaryListingMetricsDisplayStrings } from 'src/utils/ticketMetricUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { Event, ListingMetrics, Venue } from 'src/WebApiController';

import { PurchaseEventTabs } from '../PurchaseEventPage';
import * as styles from './PurchaseEventPageHeaderV2.css';

export const PurchaseEventPageHeaderWithNav = ({
  event,
  venue,
  tabs,
  activeTab,
  setActiveTab,
}: {
  event: Event;
  venue?: Venue;
  activeTab: PurchaseEventTabs;
  setActiveTab: (tab: PurchaseEventTabs) => void;
  tabs: TabItem<PurchaseEventTabs>[];
}) => {
  const { marketplaceEventInfo, refetchMarketplaceEventInfo } =
    useMarketplaceEventInfo(event);

  const isMobile = useMatchMedia('mobile');

  const { loginContext } = useAppContext();
  const { eventMetrics: eventMetricsMap } =
    useCatalogMetricsContext<ListingMetrics>();
  const eventMetrics = eventMetricsMap?.[event?.virtId ?? ''];
  const formattedTotalMetrics = eventMetrics
    ? getInventorySummaryListingMetricsDisplayStrings(
        eventMetrics,
        loginContext?.user?.activeAccount?.currencyCode
      )
    : undefined;

  const toggleGroupOptions = useMemo(() => {
    return (
      tabs.filter((tab) => tab.value !== PurchaseEventTabs.Purchases) ?? []
    )
      .map(({ value, Icon, contentId, useStroke }) => {
        if (!Icon || !contentId) {
          return null;
        }
        return {
          value,
          children: (
            <EventHeaderIconWithHoverEffect
              IconComponent={Icon!}
              selected={value === activeTab}
              useStroke={useStroke}
              contentId={contentId}
              size={vars.iconSize.l}
            />
          ),
        };
      })
      .filter(
        (item: ToggleItem | null): item is ToggleItem => item != null
      ) as ToggleItem[];
  }, [activeTab, tabs]);

  const isOnMyPurchases = useMemo(
    () => activeTab === PurchaseEventTabs.Purchases,
    [activeTab]
  );

  if (isMobile) {
    return (
      <Stack direction="column" gap="m">
        <EventHeaderMarketplacesWithMetrics
          justifyContent="spaceBetween"
          className={styles.eventHeaderNavCard}
          event={event}
          venue={venue}
          marketplaceEventInfo={marketplaceEventInfo}
          onRefetchMarketplaceEventInfo={refetchMarketplaceEventInfo}
          metrics={{
            type: EventHeaderMetricsType.Summary,
            metrics: {
              label: ContentId.Tickets,
              value: formattedTotalMetrics?.formattedTotalTicketQuantity ?? 0,
            },
          }}
        />
        {isOnMyPurchases && (
          <Stack
            direction="row"
            justifyContent="spaceBetween"
            alignItems="center"
          >
            <Stack direction="row" alignItems="center" gap="m">
              {event?.virtId && (
                <BulkActionToggleButton groupId={event.virtId} />
              )}
            </Stack>
            <EventHeaderSidePanelSwiper contentId={ContentId.ViewSales} />
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <Stack
      direction="responsive"
      alignItems="center"
      justifyContent="spaceBetween"
      className={styles.eventHeaderCard}
    >
      <Stack direction="row" alignItems="center" gap="m">
        <EventHeaderMyEntityToggleItem
          selected={activeTab === PurchaseEventTabs.Purchases}
          contentId={ContentId.MyPurchases}
          onClick={() => setActiveTab(PurchaseEventTabs.Purchases)}
        />
        <BulkActionToggleButton groupId={event.virtId} />
        <ToggleGroup
          value={activeTab}
          itemClassName={styles.toggleGroupItemParent}
          itemSelectedClassName={styles.toggleGroupItemSelected}
          onValueChange={(value) => {
            if (value) {
              setActiveTab(value as PurchaseEventTabs);
            }
          }}
          options={toggleGroupOptions}
        />
        {isOnMyPurchases && (
          <EventHeaderTableColumnAction<CustomPurchasesColumn>
            sectionType={SectionType.Purchases}
          />
        )}
      </Stack>
      <EventHeaderMarketplacesWithMetrics
        gap="m"
        event={event}
        venue={venue}
        marketplaceEventInfo={marketplaceEventInfo}
        onRefetchMarketplaceEventInfo={refetchMarketplaceEventInfo}
        metrics={{
          type: EventHeaderMetricsType.Summary,
          metrics: {
            label: ContentId.Tickets,
            value: formattedTotalMetrics?.formattedTotalTicketQuantity ?? 0,
          },
        }}
      />
    </Stack>
  );
};
